import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { PhoneNumberUtils } from '@buspro/shared/common';
import { TenantUsersDto } from '@buspro/web/apis';
import { ParcelDeliveryOrderDto, ParcelDeliveryOrdersApi, ParcelItemDto } from '@buspro/web/apis';
import { QrCodeConfigService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { QRCodeModule } from 'angularx-qrcode';
import { NgxPrinterModule } from 'ngx-printer';
import { Observable, lastValueFrom } from 'rxjs';
import { PrintService } from './print.service';

@Component({
  selector: 'tenant-parcel-receipt-print-sheet',
  standalone: true,
  imports: [CommonModule, NgxPrinterModule, QRCodeModule],
  template: `
    <div class="invoice bg-gray-100">
      <div class="print-button-container">
        <button
          class="btn btn-primary btn-lg shadow-lg"
          (click)="printService.onDataReady()"
        >
          <span class="fe fe-printer"></span> Print Receipt
        </button>
      </div>

      <div *ngIf="parcelQuery.isFetched()">
        <div
          id="parcel-receipt-section"
          class="card bg-white w-full py-4 px-4 mx-auto space-y-4 border-2 border-dashed border-gray-400"
          #parcelReceiptSection
          [ngStyle]="{ width: '80mm' }"
        >
          <!-- Company Branding -->
          <div class="flex justify-between items-start space-x-4">
            <div>
              <img
                [src]="user.company.logoUrl"
                alt="Swift Transit Logo"
                class="h-12"
              />
            </div>
            <div class="text-left">
              <h2 class="text-lg font-bold">{{ user.company.name }}</h2>
              <p>{{ user.company.motto }}</p>
            </div>
          </div>

          <hr />
          <!-- Receipt Details -->
          <div class="mb-4">
            <h3 class="text-base font-semibold mb-2">Receipt Details</h3>
            <p>
              <strong>Date:</strong>
              {{ parcel.originCollectedTimestamp! | date : 'medium' }}
            </p>
            <p><strong>Parcel No:</strong>
             <!-- {{ parcel.parcelNo }} -->

            </p>
            <p><strong>Route:</strong> {{ parcel.operationRouteName }}</p>
          </div>

          <hr />
          <!-- Sender and Receiver Info -->
          <div class="grid grid-cols-1 gap-4 mb-4">
            <div>
              <h4 class="font-semibold">Sender</h4>
              <div>
                <b>{{ parcel.senderName }}</b> - {{ formatPhoneForDisplay(parcel.senderPhone) }}
              </div>
            </div>
            <div>
              <h4 class="font-semibold">Receiver</h4>
              <p>
                <b>{{ parcel.receiverName }}</b> - ({{ formatPhoneForDisplay(parcel.receiverPhone) }})
              </p>
            </div>
          </div>

          <hr />
          <!-- Parcel Items -->
          <div class="mb-4">
            <h3 class="text-base font-semibold mb-2">Parcel Items</h3>
            <table class="w-full ">
              <tbody>
                <tr *ngFor="let item of parcel.items">
                  <td colspan="2">
                    <div class="font-semibold">{{ item.description }}</div>
                    <div class="">{{ item.totalCost | currency : 'UGX' }}</div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="1" class="text-right font-semibold">
                    Grand Total:
                  </td>
                  <td class="text-right text-lg font-bold">
                    {{ calculateGrandTotal(parcel.items) | currency : 'UGX' }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <hr />
          <!-- QR Code -->
          <div class="flex flex-col justify-between items-center mt-4">
            <qrcode
              [qrdata]="getQrCodeValue(parcel)"
              [width]="128"
              [errorCorrectionLevel]="'M'"
            ></qrcode>
            <div class="text-center mt-4">
              <p class="font-bold">
                Thank you for choosing {{ user.company.name }}!
              </p>
              <p *ngIf="user.company.phoneNumber">
                For inquiries, call: {{ user.company.phoneNumber }}
              </p>
              <p *ngIf="user.company.websiteUrl">
                Track your parcel at: {{ user.company.websiteUrl }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: `

    .invoice {
      page-break-after: always;
    }

      .invoice:last-child {
        page-break-after: avoid;
      }

      .print-button-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
      }

      .btn-lg {
        padding: 0.75rem 1.5rem;
        font-size: 1.25rem;
      }

@media print {
  .print-button-container {
    display: none;
  }

  #parcel-receipt-section {
    border: none !important;
    width: 88mm;
    height: auto;
  }

  @page {
    size: 88mm auto;
    margin: 0;
  }
}
  `,
})
export class ParcelReceiptPrintSheetComponent {
  private parcelsApi = inject(ParcelDeliveryOrdersApi);
  private ngxAuthService = inject(NgxAuthService);
  printService = inject(PrintService);
  private qrCodeConfigService = inject(QrCodeConfigService);

  parcelId = signal<string | null>(null);

  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;
  userSignal = toSignal(this.authUser$, { initialValue: null });

  parcelQuery = injectQuery(() => ({
    queryKey: ['parcel-receipt-to-print', this.parcelId()],
    queryFn: () =>
      lastValueFrom(
        this.parcelsApi.parcelDeliveryOrderControllerGetSingleParcelById({
          id: this.parcelId()!,
        }),
      ),
    select: (data: ParcelDeliveryOrderDto) => {
      if (data) {
        this.printService.onDataReady();
        setTimeout(() => {
          if (this.printService.isPrinting()) {
            console.log('Print dialog may not have opened. Please use the print button.');
          }
        }, 2000);
      }
      return data;
    },
    enabled: !!this.parcelId(),
  }));

  get user() {
    return this.userSignal()!;
  }

  get parcel() {
    return this.parcelQuery.data()!;
  }

  constructor(route: ActivatedRoute) {
    this.parcelId.set(route.snapshot.params['parcelId']);
  }

  calculateGrandTotal(items: ParcelItemDto[]): number {
    return items.reduce((total, item) => total + (item.totalCost || 0), 0);
  }

  getQrCodeValue(parcel: ParcelDeliveryOrderDto): string {
    return this.qrCodeConfigService.generateDeliveryOrderTrackingUrl(parcel.id);
  }

  formatPhoneForDisplay(phoneNumber: string): string {
    if (!phoneNumber) return '';
    // For display, we'll use Uganda as default country for formatting
    return PhoneNumberUtils.formatForDisplay(phoneNumber, 'Uganda');
  }
}
